/**
 * Which group you are in, and a way out of it — on a phone.
 *
 * The sidebar carries the switcher on a desktop and is not on screen on a
 * phone, so changing group meant going Home and picking one from the grid,
 * every time. The name at the top of the gallery said which group you were in
 * and was not a control, which is the worst combination: the information is
 * there, it looks like it should be pressable, and it is not.
 *
 * The albums screen did not even say. It returns before the gallery header
 * renders, so it showed "Albums" and a count and nothing about where you were.
 * Both use this now.
 *
 * A sheet rather than a dropdown, because a list of groups on a phone wants the
 * bottom of the screen where a thumb is, and because it can hold a search field
 * without becoming a menu that scrolls off the top.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { Input } from "../../components/ui/Input.js";
import { Sheet } from "../../components/ui/Sheet.js";
import "./GroupBar.css";

/** Enough groups that finding one by eye stops being instant. */
const SEARCH_THRESHOLD = 6;

export function GroupBar({ groupId }: { groupId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const groups = useQuery(trpc.groups.list.queryOptions());
  const all = groups.data ?? [];
  const current = all.find((group) => group.id === groupId);

  const needle = query.trim().toLocaleLowerCase();
  const shown = useMemo(
    () =>
      needle.length === 0
        ? all
        : all.filter((group) =>
            group.name.toLocaleLowerCase().includes(needle),
          ),
    [all, needle],
  );

  /*
   * Shown even with one group, where it is a label rather than a control.
   *
   * The heading below relies on this being here to say which group you are in
   * — that is how it stopped saying the name twice — so a bar that vanishes
   * for somebody with a single group would take the name with it.
   */
  const canSwitch = all.length > 1;

  const go = (id: string) => {
    setOpen(false);
    setQuery("");
    if (id !== groupId) {
      void navigate({ to: "/g/$groupId", params: { groupId: id }, search: {} });
    }
  };

  return (
    <>
      <button
        type="button"
        className="fc-groupbar"
        aria-haspopup={canSwitch ? "dialog" : undefined}
        aria-expanded={canSwitch ? open : undefined}
        disabled={!canSwitch}
        onClick={() => setOpen(true)}
      >
        <span className="fc-groupbar__icon" aria-hidden="true">
          {current?.emoji ? <EmojiIcon emoji={current.emoji} /> : "🗂"}
        </span>
        <span className="fc-groupbar__name">{current?.name ?? t("Group")}</span>
        {canSwitch && (
        <svg
          className="fc-groupbar__chevron"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M7 10l5 5 5-5" />
        </svg>
        )}
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={t("Switch group")}
        size="half"
      >
        {all.length > SEARCH_THRESHOLD && (
          <Input
            value={query}
            type="search"
            autoComplete="off"
            placeholder={t("Search groups")}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}

        <ul className="fc-groupbar__list" role="list">
          {shown.map((group) => (
            <li key={group.id}>
              <button
                type="button"
                data-current={group.id === groupId || undefined}
                onClick={() => go(group.id)}
              >
                <span className="fc-groupbar__icon" aria-hidden="true">
                  {group.emoji ? <EmojiIcon emoji={group.emoji} /> : "🗂"}
                </span>

                <span className="fc-groupbar__label">
                  <span className="fc-groupbar__title">{group.name}</span>
                  <span className="fc-groupbar__count fc-tabular">
                    {group.fileCount.toLocaleString()}{" "}
                    {group.fileCount === 1 ? t("item") : t("items")}
                  </span>
                </span>

                {/*
                  A tick rather than a highlight. The current group is the one
                  somebody is looking at, and dimming or colouring its row
                  reads as "unavailable" more often than as "you are here".
                */}
                {group.id === groupId && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
    </>
  );
}
